import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Inject } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { OrderTicket } from "@models/order";
import { SnackBarService } from "@services/snack-bar.service";
import {
  TicketManagementService,
  TransferTicketOwnerRequest,
} from "@services/ticket-management.service";
import { phoneNumberValidator } from "@shared/validatos";
import { fieldsMatchValidator } from "@shared/validatos/fields-match.validator";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { finalize } from "rxjs";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { DdiSelectComponent } from "../../../../../shared/components/ddi-select/ddi-select.component";
import { DialogHeaderComponent } from "../../../../../shared/components/dialog-header/dialog-header.component";

export interface TicketTransferDialogData {
  ticket: OrderTicket;
}

@Component({
  selector: "app-ticket-transfer-dialog",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    DdiSelectComponent,
    NgxMaskDirective,
    MatDialogModule,
    DialogHeaderComponent,
  ],
  templateUrl: "./ticket-transfer-dialog.component.html",
  styleUrl: "./ticket-transfer-dialog.component.scss",
  providers: [provideNgxMask()],
})
export class TicketTransferDialogComponent {
  readonly dialogRef = inject(MatDialogRef<TicketTransferDialogComponent>);

  private readonly snackbarService = inject(SnackBarService);
  private readonly ticketService = inject(TicketManagementService);
  private readonly fb = inject(FormBuilder);

  // Dialog data
  ticket: OrderTicket;

  constructor(@Inject(MAT_DIALOG_DATA) public data: TicketTransferDialogData) {
    this.ticket = data.ticket;
  }

  isLoading = false;

  assignForm = this.fb.group(
    {
      user_email: ["", [Validators.required, Validators.email]],
      user_email_confirm: ["", [Validators.required, Validators.email]],
      ddi: ["+55", [Validators.required]],
      user_phone_number: ["", [Validators.required, phoneNumberValidator]],
    },
    {
      validators: fieldsMatchValidator("user_email", "user_email_confirm"),
    },
  );

  onSubmitAssign() {
    this.assignForm.markAllAsTouched();

    if (this.assignForm.invalid) {
      this.snackbarService.openSnackBar(
        "Formulário inválido!",
        "Fechar",
        "error",
      );
      return;
    }

    const { ddi, user_email, user_phone_number } = this.assignForm.value;

    const transferRequest: TransferTicketOwnerRequest = {
      user_email: user_email!,
      user_phone_number: ddi! + user_phone_number,
    };

    this.isLoading = true;

    this.ticketService
      .transferTicket(this.ticket.id, transferRequest)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (_) => {
          this.snackbarService.openSnackBar(
            "Ticket transferido com sucesso!",
            "Fechar",
            "success",
          );
          this.dialogRef.close(true);
        },
        error: (error: HttpErrorResponse) => {
          const translatedError =
            error.error.error ===
            "There is already a transfer in progress for this ticket"
              ? "Já existe uma transferência em progresso para esse ingresso"
              : undefined;
          this.snackbarService.openSnackBar(
            translatedError || error.error.error,
            "Fechar",
            "error",
          );
        },
      });
  }
}
